import React, { useContext, useMemo } from 'react';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { BREAKPOINT_MAP, CLICK_PORTAL_CARD_LAYOUT, FIELD_NAMES } from 'consts';
import { GetSchoolListingResults } from 'types';
import ClickPortalResultsSection from './ClickPortalResultsSection';
import { isClickPortalResultMatch } from './isClickPortalResultMatch';

const loadingData = [{ id: '1' }, { id: '2' }, { id: '3' }];

export interface ClickPortalVerticalResultsProps {
  firstRow?: GetSchoolListingResults | null;
  isLoading?: boolean;
  cardLayout?: CLICK_PORTAL_CARD_LAYOUT;
}

/**
 * Single-row click portal listing: matches / non-matches columns of result cards.
 * Reads the same global listing state as {@link ClickPortalResults}, or optional
 * `firstRow` / `isLoading` from Portal Results when hydrating from section SSR data.
 */
export default function ClickPortalVerticalResults(
  props: ClickPortalVerticalResultsProps = {}
): React.JSX.Element {
  const {
    clickPortal,
    siteMeta: { disclosure, clickPortalSettings },
    windowSize: { currentBreakpoint },
  } = useContext(GlobalContext);

  const firstRow = props.firstRow ?? clickPortal.firstRow;
  const isLoading = props.isLoading ?? clickPortal.isLoading;
  const { currentSelection } = clickPortal;
  const showRelatedResults = clickPortalSettings?.showRelatedResults ?? true;

  const groupedResults = useMemo(() => {
    function isMatch(
      school: GetSchoolListingResults['results'][number]
    ): boolean {
      if (!firstRow) {
        return false;
      }

      const matchingConcentration = (
        currentSelection[FIELD_NAMES.CLICK_PORTAL_CONCENTRATIONS] as {
          value: string | null;
        }
      )?.value;

      return isClickPortalResultMatch(school, {
        hasTags: firstRow.hasTags,
        matchingConcentration,
      });
    }

    if (isLoading) {
      return {
        matches: loadingData,
        nonMatches: showRelatedResults ? loadingData : [],
      };
    }
    const groups = firstRow?.results?.reduce(
      (acc, school) => {
        if (!showRelatedResults || isMatch(school)) {
          acc.matches.push(school);
        } else {
          acc.nonMatches.push(school);
        }

        return acc;
      },
      { matches: [], nonMatches: [] } as {
        matches: GetSchoolListingResults['results'][number][];
        nonMatches: GetSchoolListingResults['results'][number][];
      }
    );
    return groups;
  }, [firstRow, currentSelection, isLoading, showRelatedResults]);

  return (
    <>
      {groupedResults?.matches?.length > 0 && (
        <ClickPortalResultsSection
          results={groupedResults.matches}
          isLoading={isLoading}
          disclosure={disclosure}
          cardLayout={props.cardLayout}
        />
      )}
      {groupedResults?.nonMatches?.length > 0 && (
        <ClickPortalResultsSection
          results={groupedResults.nonMatches}
          resultType="nonMatches"
          isLoading={isLoading}
          cardLayout={props.cardLayout}
          title={
            currentBreakpoint === BREAKPOINT_MAP.DESKTOP.label
              ? `Other schools with programs you may be interested in:`
              : `Other programs you may be interested in:`
          }
        />
      )}
    </>
  );
}
